(() => {
  const SELECTOR = '#FacetSortForm #SortBy';
  const READY_ATTRIBUTE = 'data-custom-sort-ready';

  let activeDropdown = null;
  let dropdownCounter = 0;

  class CustomSortDropdown {
    constructor(select) {
      this.select = select;
      this.wrapper = select.closest('.select');

      if (!this.wrapper) return;

      this.options = Array.from(this.select.options);
      this.highlightedIndex = Math.max(this.select.selectedIndex, 0);
      this.isOpen = false;

      this.build();
      this.bindEvents();
      this.syncFromSelect();
    }

    build() {
      dropdownCounter += 1;

      this.listboxId = `CustomSortOptions-${dropdownCounter}`;

      this.wrapper.classList.add('custom-sort');
      this.wrapper.setAttribute(READY_ATTRIBUTE, 'true');

      this.select.classList.add('custom-sort__native');
      this.select.setAttribute('tabindex', '-1');
      this.select.setAttribute('aria-hidden', 'true');

      const existingCaret = this.wrapper.querySelector('.icon-caret');

      if (existingCaret) {
        existingCaret.classList.add('custom-sort__original-caret');
      }

      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'custom-sort__button';
      this.button.setAttribute('aria-haspopup', 'listbox');
      this.button.setAttribute('aria-expanded', 'false');
      this.button.setAttribute('aria-controls', this.listboxId);

      this.buttonLabel = document.createElement('span');
      this.buttonLabel.className = 'custom-sort__button-label';

      this.buttonCaret = document.createElement('span');
      this.buttonCaret.className = 'custom-sort__button-caret';
      this.buttonCaret.setAttribute('aria-hidden', 'true');

      this.button.append(this.buttonLabel, this.buttonCaret);

      this.menu = document.createElement('div');
      this.menu.className = 'custom-sort__menu';
      this.menu.hidden = true;

      this.listbox = document.createElement('div');
      this.listbox.id = this.listboxId;
      this.listbox.className = 'custom-sort__list';
      this.listbox.setAttribute('role', 'listbox');
      this.listbox.setAttribute(
        'aria-label',
        this.select.getAttribute('aria-label') || 'Sort products'
      );

      this.optionButtons = this.options.map((option, index) => {
        const optionButton = document.createElement('button');

        optionButton.type = 'button';
        optionButton.className = 'custom-sort__option';
        optionButton.setAttribute('role', 'option');
        optionButton.setAttribute('aria-selected', 'false');
        optionButton.dataset.index = String(index);
        optionButton.dataset.value = option.value;
        optionButton.textContent = option.textContent.trim();

        this.listbox.appendChild(optionButton);

        return optionButton;
      });

      this.menu.appendChild(this.listbox);
      this.wrapper.append(this.button, this.menu);
    }

    bindEvents() {
      this.button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();

        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
      });

      this.button.addEventListener('keydown', event => {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();

            if (!this.isOpen) {
              this.open();
            } else {
              this.moveHighlight(1);
            }
            break;

          case 'ArrowUp':
            event.preventDefault();

            if (!this.isOpen) {
              this.open();
            } else {
              this.moveHighlight(-1);
            }
            break;

          case 'Enter':
          case ' ':
            event.preventDefault();

            if (!this.isOpen) {
              this.open();
            } else {
              this.chooseHighlighted();
            }
            break;

          case 'Escape':
            if (this.isOpen) {
              event.preventDefault();
              this.close(true);
            }
            break;
        }
      });

      this.listbox.addEventListener('click', event => {
        const optionButton = event.target.closest('.custom-sort__option');

        if (!optionButton) return;

        event.preventDefault();
        event.stopPropagation();

        const index = Number(optionButton.dataset.index);

        this.selectOption(index);
      });

      this.listbox.addEventListener('mousemove', event => {
        const optionButton = event.target.closest('.custom-sort__option');

        if (!optionButton) return;

        this.highlightOption(Number(optionButton.dataset.index));
      });

      this.listbox.addEventListener('keydown', event => {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            this.moveHighlight(1);
            break;

          case 'ArrowUp':
            event.preventDefault();
            this.moveHighlight(-1);
            break;

          case 'Home':
            event.preventDefault();
            this.highlightAndFocus(0);
            break;

          case 'End':
            event.preventDefault();
            this.highlightAndFocus(this.optionButtons.length - 1);
            break;

          case 'Enter':
          case ' ':
            event.preventDefault();
            this.chooseHighlighted();
            break;

          case 'Escape':
            event.preventDefault();
            this.close(true);
            break;

          case 'Tab':
            this.close();
            break;
        }
      });

      this.select.addEventListener('input', () => {
        this.syncFromSelect();
      });

      this.select.addEventListener('change', () => {
        this.syncFromSelect();
      });
    }

    open() {
      if (activeDropdown && activeDropdown !== this) {
        activeDropdown.close();
      }

      activeDropdown = this;
      this.isOpen = true;

      this.wrapper.classList.add('custom-sort--open');
      this.button.setAttribute('aria-expanded', 'true');
      this.menu.hidden = false;

      this.highlightOption(Math.max(this.select.selectedIndex, 0));

      requestAnimationFrame(() => {
        this.focusHighlighted();
      });
    }

    close(returnFocus = false) {
      this.isOpen = false;

      this.wrapper.classList.remove('custom-sort--open');
      this.button.setAttribute('aria-expanded', 'false');
      this.menu.hidden = true;

      if (activeDropdown === this) {
        activeDropdown = null;
      }

      if (returnFocus && document.contains(this.button)) {
        this.button.focus();
      }
    }

    moveHighlight(direction) {
      if (!this.optionButtons.length) return;

      let nextIndex = this.highlightedIndex + direction;

      if (nextIndex < 0) {
        nextIndex = this.optionButtons.length - 1;
      }

      if (nextIndex >= this.optionButtons.length) {
        nextIndex = 0;
      }

      this.highlightAndFocus(nextIndex);
    }

    highlightAndFocus(index) {
      this.highlightOption(index);
      this.focusHighlighted();
    }

    highlightOption(index) {
      if (!this.optionButtons[index]) return;

      this.highlightedIndex = index;

      this.optionButtons.forEach((button, buttonIndex) => {
        button.classList.toggle(
          'custom-sort__option--highlighted',
          buttonIndex === index
        );
      });
    }

    focusHighlighted() {
      const highlightedButton =
        this.optionButtons[this.highlightedIndex];

      if (!highlightedButton) return;

      highlightedButton.focus({
        preventScroll: true
      });

      highlightedButton.scrollIntoView({
        block: 'nearest'
      });
    }

    chooseHighlighted() {
      this.selectOption(this.highlightedIndex);
    }

    selectOption(index) {
      const selectedOption = this.options[index];

      if (!selectedOption) return;

      const valueChanged =
        this.select.value !== selectedOption.value;

      this.select.value = selectedOption.value;
      this.select.selectedIndex = index;

      this.syncFromSelect();
      this.close();

      if (!valueChanged) {
        this.button.focus();
        return;
      }

      /*
       * Update the current collection URL directly. This keeps all active
       * filter parameters intact and avoids depending on the mobile overlay
       * or Shopify's AJAX facet lifecycle for desktop sorting.
       */
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', selectedOption.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    }

    syncFromSelect() {
      this.options = Array.from(this.select.options);

      const selectedIndex = Math.max(
        this.select.selectedIndex,
        0
      );

      const selectedOption = this.options[selectedIndex];

      if (!selectedOption) return;

      this.highlightedIndex = selectedIndex;
      this.buttonLabel.textContent =
        selectedOption.textContent.trim();

      this.optionButtons.forEach((button, index) => {
        const isSelected = index === selectedIndex;

        button.classList.toggle(
          'custom-sort__option--selected',
          isSelected
        );

        button.setAttribute(
          'aria-selected',
          isSelected ? 'true' : 'false'
        );
      });
    }
  }

  function initialiseCustomSort(root = document) {
    const selects = [];

    if (root.matches?.(SELECTOR)) {
      selects.push(root);
    }

    root.querySelectorAll?.(SELECTOR).forEach(select => {
      selects.push(select);
    });

    selects.forEach(select => {
      const wrapper = select.closest('.select');

      if (
        !wrapper ||
        wrapper.hasAttribute(READY_ATTRIBUTE)
      ) {
        return;
      }

      new CustomSortDropdown(select);
    });
  }

  function closeOnOutsideClick(event) {
    if (!activeDropdown) return;

    if (!activeDropdown.wrapper.contains(event.target)) {
      activeDropdown.close();
    }
  }

  function observeAjaxUpdates() {
    const observer = new MutationObserver(mutations => {
      let shouldInitialise = false;

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;

          if (
            node.matches?.('#FacetSortForm') ||
            node.matches?.(SELECTOR) ||
            node.querySelector?.('#FacetSortForm') ||
            node.querySelector?.(SELECTOR)
          ) {
            shouldInitialise = true;
            break;
          }
        }

        if (shouldInitialise) break;
      }

      if (shouldInitialise) {
        initialiseCustomSort(document);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  document.addEventListener('click', closeOnOutsideClick);

  document.addEventListener('shopify:section:load', event => {
    initialiseCustomSort(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialiseCustomSort(document);
      observeAjaxUpdates();
    });
  } else {
    initialiseCustomSort(document);
    observeAjaxUpdates();
  }
})();
/* =========================================================
 * Mobile collection sort popup
 * Kept completely separate from the desktop custom sort.
 * Mobile choices update the URL directly, so the desktop
 * #FacetSortForm control and its event handling are untouched.
 * ========================================================= */
(() => {
  const MOBILE_QUERY = window.matchMedia('(max-width: 989px)');
  const READY_ATTRIBUTE = 'data-mobile-sort-ready';
  const BODY_CLASS = 'mobile-sort-open';

  function initialiseMobileSort(root = document) {
    const buttons = [];

    if (root.matches?.('#MobileSortButton')) {
      buttons.push(root);
    }

    root.querySelectorAll?.('#MobileSortButton').forEach(button => {
      buttons.push(button);
    });

    buttons.forEach(button => {
      if (button.hasAttribute(READY_ATTRIBUTE)) return;

      const toolbarRoot = button.closest('menu-drawer') || document;
      const popup = toolbarRoot.querySelector('#MobileSortPopup');
      const panel = toolbarRoot.querySelector('.mobile-sort-popup__panel');
      const closeButton = toolbarRoot.querySelector('#MobileSortClose');
      const mobileSelect = toolbarRoot.querySelector('#MobileSortSelect');
      const applyButton = toolbarRoot.querySelector('#MobileSortApply');
      const radioInputs = Array.from(
        toolbarRoot.querySelectorAll('.mobile-sort-popup__radio')
      );

      if (
        !popup ||
        !panel ||
        !closeButton ||
        !mobileSelect ||
        !applyButton ||
        !radioInputs.length
      ) {
        return;
      }

      button.setAttribute(READY_ATTRIBUTE, 'true');

      const currentValue = () => {
        const urlValue = new URL(window.location.href).searchParams.get('sort_by');
        return urlValue || mobileSelect.value;
      };

      const syncControls = () => {
        const value = currentValue();
        const hasOption = Array.from(mobileSelect.options).some(
          option => option.value === value
        );

        if (hasOption) {
          mobileSelect.value = value;
        }

        radioInputs.forEach(radio => {
          radio.checked = radio.value === mobileSelect.value;
        });
      };

      const closePopup = (returnFocus = false) => {
        popup.hidden = true;
        popup.setAttribute('aria-hidden', 'true');
        button.setAttribute('aria-expanded', 'false');
        document.body.classList.remove(BODY_CLASS);

        if (returnFocus && document.contains(button)) {
          button.focus({ preventScroll: true });
        }
      };

      const openPopup = () => {
        if (!MOBILE_QUERY.matches) return;

        syncControls();
        popup.hidden = false;
        popup.setAttribute('aria-hidden', 'false');
        button.setAttribute('aria-expanded', 'true');
        document.body.classList.add(BODY_CLASS);

        requestAnimationFrame(() => {
          const checkedRadio = radioInputs.find(radio => radio.checked);
          (checkedRadio || closeButton).focus({ preventScroll: true });
        });
      };

      const applySort = selectedValue => {
        if (!selectedValue) return;

        mobileSelect.value = selectedValue;
        closePopup();

        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', selectedValue);
        window.location.assign(url.toString());
      };

      button.addEventListener('click', event => {
        if (!MOBILE_QUERY.matches) return;

        event.preventDefault();
        event.stopPropagation();
        openPopup();
      });

      closeButton.addEventListener('click', event => {
        event.preventDefault();
        closePopup(true);
      });

      radioInputs.forEach(radio => {
        radio.addEventListener('change', event => {
          if (!event.currentTarget.checked) return;
          applySort(event.currentTarget.value);
        });
      });

      applyButton.addEventListener('click', event => {
        event.preventDefault();
        applySort(mobileSelect.value);
      });

      popup.addEventListener('click', event => {
        if (event.target === popup) {
          closePopup(true);
        }
      });

      panel.addEventListener('click', event => {
        event.stopPropagation();
      });

      popup.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closePopup(true);
        }
      });

      MOBILE_QUERY.addEventListener?.('change', event => {
        if (!event.matches) {
          closePopup(false);
        }
      });
    });
  }

  function observeMobileSortUpdates() {
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;

          if (
            node.matches?.('#MobileSortButton') ||
            node.querySelector?.('#MobileSortButton')
          ) {
            initialiseMobileSort(node);
            return;
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  window.addEventListener('pageshow', () => {
    document.body.classList.remove(BODY_CLASS);
    document.querySelectorAll('#MobileSortPopup').forEach(popup => {
      popup.hidden = true;
      popup.setAttribute('aria-hidden', 'true');
    });
  });

  document.addEventListener('shopify:section:load', event => {
    initialiseMobileSort(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialiseMobileSort(document);
      observeMobileSortUpdates();
    });
  } else {
    initialiseMobileSort(document);
    observeMobileSortUpdates();
  }
})();
